export interface AvisoDeFallo {
  title: string
  message: string
  style: 'error' | 'warning'
}

/** Forma de lo que RTK Query deja en `action.payload` al rechazar. */
export interface PayloadDeFallo {
  status?: number | string
  data?: { title?: string; detail?: string }
  error?: string
}

/**
 * Los códigos que el middleware ya atiende con su propia rama y su propio texto.
 * El aviso genérico solo cubre lo que queda fuera.
 */
export const CODIGOS_CON_RAMA_PROPIA = new Set<number>([400, 401, 500])

const MENSAJES_POR_CODIGO: Record<number, string> = {
  403: 'No tiene permisos para esta acción. Si su rol cambió hace poco, vuelva a iniciar sesión.',
  404: 'El registro ya no existe, o la dirección del servicio cambió. Vuelva a cargar la pantalla.',
  409: 'Otro usuario modificó este registro antes que usted. Vuelva a cargar y reintente.',
  422: 'El servidor rechazó los datos enviados.',
}

/**
 * Los `status` que RTK Query pone cuando el fallo es del transporte y no del backend. Llegan como
 * STRING, que es la razón de fondo por la que se perdían: ninguna comparación numérica los atrapa.
 */
const MENSAJES_POR_TIPO: Record<string, string> = {
  FETCH_ERROR: 'No se pudo conectar con el servidor. Revise su conexión e intente nuevamente.',
  TIMEOUT_ERROR: 'El servidor tardó demasiado en responder. Intente nuevamente.',
  PARSING_ERROR: 'El servidor respondió en un formato inesperado. Avise a TI.',
  CUSTOM_ERROR: 'La operación no se pudo completar.',
}

/** Si este fallo necesita el aviso genérico, o ya lo cubre una rama propia del middleware. */
export const necesitaAvisoGenerico = (payload?: PayloadDeFallo): boolean => {
  if (!payload) {
    return true
  }

  return !(typeof payload.status === 'number' && CODIGOS_CON_RAMA_PROPIA.has(payload.status))
}

/**
 * El aviso a mostrar para un rechazo que ninguna rama específica atiende.
 *
 * Antes esos rechazos se perdían EN SILENCIO: el middleware solo tenía ramas para 400, 401 y 500,
 * así que un 403 por desfase de roles, un 404, un 409 o un fallo de red no producían ninguna señal.
 * Cada pantalla que lo notaba se parcheaba a mano, y las que no, dejaban al usuario reintentando
 * sin entender por qué su cambio no quedaba.
 *
 * No se reutiliza `data.detail` a ciegas: en un fallo de transporte el payload no trae `data`, así
 * que el mensaje habría salido vacío. Se prefiere el texto del backend cuando existe y, si no, uno
 * que diga qué pasó y qué puede hacer el usuario.
 */
export const avisoDelFalloRtk = (payload?: PayloadDeFallo): AvisoDeFallo => {
  const { status, data } = payload ?? {}
  const detalleDelBackend = data?.detail?.trim()

  const mensaje =
    detalleDelBackend ||
    (typeof status === 'number' ? MENSAJES_POR_CODIGO[status] : MENSAJES_POR_TIPO[String(status)]) ||
    `La operación no se pudo completar${status === undefined ? '' : ` (${status})`}. Intente nuevamente o avise a TI.`

  return {
    title: data?.title ?? 'Reservas',
    // Un 403 o un 409 son situaciones esperables que el usuario puede resolver; el resto es un
    // fallo que no controla.
    style: typeof status === 'number' && (status === 403 || status === 409) ? 'warning' : 'error',
    message: mensaje,
  }
}
